import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AccessList, ListedGroup, Permission } from "../../../api/access/accessModel";
import AppButton from "../../../ui/buttons/AppButton";
import ToggleSwitch from "../../../ui/forms/ToggleSwitch";
import ListCard from "../../../ui/layout/ListCard";
import { itemKindText } from "../../workflows";
import { grantsChange } from "../changes/accessChanges";
import type { AccessChanges } from "../changes/useAccessChanges";
import { curatedKinds, holds, withCuratedKinds, withPermission } from "../model/grantSets";
import type { GroupNamer } from "../model/groupNames";
import { INSTALLATION_PERMISSIONS, NEEDS_REASON, PROJECT_PERMISSIONS_BY_CATEGORY } from "../model/permissionCatalogue";
import CurateKindsDialog from "./CurateKindsDialog";
import PermissionLine, { CategoryHeading } from "./PermissionLine";

interface GroupPermissionsProps {
  group: ListedGroup;
  list: AccessList;
  namer: GroupNamer;
  changes: AccessChanges;
}

/**
 * Everything a group's members get, as switches on one screen (redesign R3-3 `group`): a
 * project group's permissions by category; an installation group's own permissions, then
 * those it brings to every project that includes it. A switch applies at once with Undo. The
 * viewer gets a working switch only for what the server says they may give (`givable`) on a
 * group they manage; fixed grants show locked.
 */
export default function GroupPermissions({ group, list, namer, changes }: GroupPermissionsProps) {
  const { t } = useTranslation();
  const [choosingKinds, setChoosingKinds] = useState(false);
  const named = { groupId: group.groupId, name: namer.name(group.groupId) };
  const fixed = new Set(group.fixed.map((grant) => grant.permission));
  // The installation's Administrators hold every installation permission; nobody changes that.
  const unchangeable = group.system === "administrators";
  const kinds = curatedKinds(group.grants, list.itemKinds);

  // A project's Administrators hold their fixed grants in their own set, so a new set keeps them.
  const change = (next: ReturnType<typeof withPermission>) => { changes.run(grantsChange(t, named, group.grants, next)); };

  const line = (permission: Permission) => {
    const isFixed = fixed.has(permission) || unchangeable;
    const checked = holds(group.grants, permission) || fixed.has(permission);
    const notGivable = !checked && !list.givable.has(permission);
    const locked = isFixed || (group.manageable && notGivable);
    return (
      <PermissionLine key={permission} permission={permission} locked={locked}
        hint={permission === NEEDS_REASON && notGivable && group.manageable ? t("access.serverKeysHint") : null}>
        {(labelId) => (
          <>
            {permission === "curate" && checked ? (
              group.manageable && !isFixed ? (
                <AppButton variant="ghost" size="sm" onClick={() => { setChoosingKinds(true); }}>{kindsText(kinds)}</AppButton>
              ) : <span className="app-text-muted text-xs">{kindsText(kinds)}</span>
            ) : null}
            <ToggleSwitch checked={checked} labelledBy={labelId} disabled={changes.busy || !group.manageable || isFixed || notGivable}
              onChange={(on) => { change(withPermission(group.grants, permission, on, list.itemKinds)); }} />
          </>
        )}
      </PermissionLine>
    );
  };

  const kindsText = (chosen: readonly string[]) => (chosen.length === list.itemKinds.length ? t("access.kinds.every") : chosen.map((kind) => itemKindText(kind, t)).join(", "));

  return (
    <ListCard title={t("access.permissionsTitle")}>
      <ul className="app-divider-top pb-2">
        {list.projectId === null && group.projectId === null ? (
          <>
            <CategoryHeading>{t("access.categories.installation")}</CategoryHeading>
            {INSTALLATION_PERMISSIONS.map(line)}
            {/* No project includes the installation's Administrators; what they may in projects is their own source (GS-1). */}
            {unchangeable ? null : (
              <>
                <CategoryHeading>{t("access.categories.inProjects")}</CategoryHeading>
                {PROJECT_PERMISSIONS_BY_CATEGORY.flatMap(({ permissions }) => permissions).map(line)}
              </>
            )}
          </>
        ) : PROJECT_PERMISSIONS_BY_CATEGORY.map(({ category, permissions }) => (
          <li key={category}>
            <ul>
              <CategoryHeading>{t(`access.categories.${category}`)}</CategoryHeading>
              {permissions.map(line)}
            </ul>
          </li>
        ))}
      </ul>
      {group.manageable && !unchangeable ? <p className="app-text-muted app-divider-top px-4 py-2 text-xs">{t("access.giveOnlyOwn")}</p> : null}
      {choosingKinds ? (
        <CurateKindsDialog title={t("access.kinds.title", { group: named.name })} itemKinds={list.itemKinds} chosen={kinds}
          onSave={(chosen) => { change(withCuratedKinds(group.grants, chosen)); }}
          onClose={() => { setChoosingKinds(false); }} />
      ) : null}
    </ListCard>
  );
}
